import { Component } from 'react';
import { connect } from 'react-redux';
import AdminTable from 'layouts/AdminTable';
import moment from 'moment';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { fetchMealTypes } from 'actions/MealTypes';
import { fetchVariants } from 'actions/Variants';
import { fetchDiets } from 'actions/Diets';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormSelectSingle from 'components/FormSelect/FormSelectSingle';
import withStyles from '@material-ui/core/styles/withStyles';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import axios from 'helpers/gastro';
import { get } from 'helpers/apiHelpers';
import { withTranslation } from 'react-i18next';
import { apiErrorToast } from 'utils/apiError';
import MenuSummaryWeekPicker from 'components/MenuSummaryWeekPicker/MenuSummaryWeekPicker';

import { isGranted } from '../../../helpers/helpers';
import { ROLE_SHOW_MENU_SUMMARY } from '../../../helpers/roles';
import SummaryContent from './SummaryContent';

const getNormalizedWeekStart = value => {
  const normalizedWeekStart = value
    ? moment(value)
    : moment().startOf('isoWeek');

  if (normalizedWeekStart.isValid()) {
    return normalizedWeekStart.startOf('isoWeek');
  }

  return moment().startOf('isoWeek');
};

const getVariantRouteId = variant => variant?.id || '';
const getNormalizedVariantRouteId = variantId =>
  variantId === undefined || variantId === null ? '' : String(variantId);

class Summary extends Component {
  state = {
    selectedWeekStart: moment().startOf('isoWeek'),
    variant: '',
    lockedExport: false,
    loading: false,
    menuPlanners: [],
  };

  summaryRequestId = 0;

  isUnmounted = false;

  findVariantByRouteId = (
    routeVariantId = this.props.match.params.variant,
    variants = this.props.variants
  ) =>
    variants.find(
      variantElement =>
        getNormalizedVariantRouteId(variantElement.id) ===
        getNormalizedVariantRouteId(routeVariantId)
    ) || '';

  async componentDidMount() {
    const { match } = this.props;
    await Promise.all([
      this.props.fetchMealTypes(),
      this.props.fetchVariants(),
      this.props.fetchDiets(),
    ]);

    const selectedWeekStart = getNormalizedWeekStart(match.params.date);
    const variant = this.findVariantByRouteId(match.params.variant);

    this.setState(
      {
        selectedWeekStart,
        variant,
      },
      () => {
        this.fetchMenuPlanners();
      }
    );
  }

  componentDidUpdate(prevProps) {
    const previousRouteVariantId = getNormalizedVariantRouteId(
      prevProps.match.params.variant
    );
    const currentRouteVariantId = getNormalizedVariantRouteId(
      this.props.match.params.variant
    );
    const currentSelectedVariantId = getNormalizedVariantRouteId(
      getVariantRouteId(this.state.variant)
    );

    if (
      currentRouteVariantId &&
      (previousRouteVariantId !== currentRouteVariantId ||
        prevProps.variants !== this.props.variants) &&
      currentSelectedVariantId !== currentRouteVariantId
    ) {
      const routeVariant = this.findVariantByRouteId(currentRouteVariantId);

      if (routeVariant) {
        this.setState(
          {
            variant: routeVariant,
          },
          () => {
            this.fetchMenuPlanners();
          }
        );
      }
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.summaryRequestId += 1;
  }

  getLastDay = (weekStart = this.state.selectedWeekStart) =>
    getNormalizedWeekStart(weekStart).clone().endOf('isoWeek');

  getVisibleDays = () => {
    const weekStart = getNormalizedWeekStart(this.state.selectedWeekStart);
    const array = [weekStart.format('YYYY-MM-DD')];

    // eslint-disable-next-line no-plusplus -- FIXME
    for (let i = 1; i < 7; i++) {
      const date = weekStart.clone().add(i, 'days');
      const key = date.locale('en').format('dddd').toLowerCase();

      if (this.props.brand[key] !== 0) {
        array.push(date.format('YYYY-MM-DD'));
      }
    }

    return array;
  };

  fetchMenuPlanners = async () => {
    const { t } = this.props;
    const { variant } = this.state;

    if (variant === '') {
      this.summaryRequestId += 1;

      if (this.state.menuPlanners.length > 0 || this.state.loading) {
        this.setState({
          menuPlanners: [],
          loading: false,
        });
      }

      return;
    }

    const requestId = this.summaryRequestId + 1;
    const weekStart = getNormalizedWeekStart(this.state.selectedWeekStart);
    const weekEnd = this.getLastDay(weekStart);

    this.summaryRequestId = requestId;
    this.setState({ loading: true });

    try {
      const response = await get('/menu-summary', {
        pagination: false,
        'date[after]': weekStart.format('YYYY-MM-DD'),
        'date[before]': weekEnd.format('YYYY-MM-DD'),
        variant: variant['@id'],
        properties: ['id', 'mealType', 'diet', 'date'],
        'properties[dish]': 'nameForClient',
      });

      if (this.isUnmounted || requestId !== this.summaryRequestId) {
        return;
      }

      this.setState({
        menuPlanners: response['hydra:member'],
        loading: false,
      });
    } catch (error) {
      if (this.isUnmounted || requestId !== this.summaryRequestId) {
        return;
      }

      apiErrorToast(t('menuPlanner.errors.somethingGoWrong'), error);
      this.setState({
        menuPlanners: [],
        loading: false,
      });
    }
  };

  updateSelection = ({
    selectedWeekStart = this.state.selectedWeekStart,
    variant = this.state.variant,
  }) => {
    const nextWeekStart = getNormalizedWeekStart(selectedWeekStart);
    const isSameWeek = nextWeekStart.isSame(
      this.state.selectedWeekStart,
      'day'
    );
    const isSameVariant =
      getVariantRouteId(variant) === getVariantRouteId(this.state.variant);

    if (isSameWeek && isSameVariant) {
      return;
    }

    this.setState(
      {
        selectedWeekStart: nextWeekStart,
        variant,
      },
      () => {
        this.fetchMenuPlanners();
      }
    );
  };

  modifyDate = add => {
    const direction = add ? 1 : -1;

    this.updateSelection({
      selectedWeekStart: this.state.selectedWeekStart
        .clone()
        .add(direction, 'week'),
    });
  };

  handleSelectChange = (event, value) => {
    this.updateSelection({
      variant: value || '',
    });
  };

  handleWeekChange = selectedWeekStart => {
    this.updateSelection({ selectedWeekStart });
  };

  exportToPDF = () => {
    const { t } = this.props;
    const weekStart = getNormalizedWeekStart(this.state.selectedWeekStart);
    const weekEnd = this.getLastDay(weekStart);

    this.setState({ lockedExport: true });

    const params = {
      'date[after]': weekStart.format('YYYY-MM-DD'),
      'date[before]': weekEnd.format('YYYY-MM-DD'),
      variant: this.state.variant.id,
    };

    axios
      .get('reports/menu-planner', {
        responseType: 'blob',
        params,
        headers: { accept: 'application/pdf' },
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(
        response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `Planowanie-menu-${weekStart.format('YYYY-MM-DD')}-${weekEnd.format(
              'YYYY-MM-DD'
            )}_(${moment().format('YYYY-MM-DD_HH.mm')}).pdf`
          );

          document.body.appendChild(link);
          link.click();
          this.setState({ lockedExport: false });
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
        error => {
          apiErrorToast(t('reports.cannotGenerate'), error);
          this.setState({ lockedExport: false });
        }
      );
  };

  exportToXLSX = () => {
    const { t } = this.props;
    const weekStart = getNormalizedWeekStart(this.state.selectedWeekStart);
    const weekEnd = this.getLastDay(weekStart);

    this.setState({ lockedExport: true });

    const params = {
      'date[after]': weekStart.format('YYYY-MM-DD'),
      'date[before]': weekEnd.format('YYYY-MM-DD'),
      variant: this.state.variant.id,
      weekView: true,
    };

    axios
      .get('reports/menu-planner', {
        responseType: 'blob',
        params,
        headers: { accept: 'application/vnd.ms-excel' },
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(
        response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `Planowanie-menu-${weekStart.format('YYYY-MM-DD')}-${weekEnd.format(
              'YYYY-MM-DD'
            )}_(${moment().format('YYYY-MM-DD_HH.mm')}).xlsx`
          );

          document.body.appendChild(link);
          link.click();
          this.setState({ lockedExport: false });
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
        error => {
          apiErrorToast(t('reports.cannotGenerate'), error);
          this.setState({ lockedExport: false });
        }
      );
  };

  render() {
    const { variants: givenVariants, diets, classes, i18n } = this.props;
    const variantOptions = givenVariants.map(variant => {
      const displayName = variant.active
        ? variant.name
        : variant.name + this.props.t('form.menuPlanning.inactive');

      return {
        ...variant,
        displayName,
      };
    });
    const filteredDiets = this.state.variant
      ? diets.filter(diet => this.state.variant.diets.includes(diet['@id']))
      : [];

    return isGranted(ROLE_SHOW_MENU_SUMMARY) ? (
      <AdminTable title={this.props.t('form.menuPlanning.menuSummary')}>
        <GridContainer>
          <GridItem md={4} style={{ textAlign: 'right' }}>
            <Button
              color="info"
              round={true}
              onClick={() => {
                this.modifyDate(false);
              }}
            >
              <KeyboardArrowLeft /> {this.props.t('form.menuPlanning.prevWeek')}
            </Button>
          </GridItem>
          <GridItem md={4} style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '15px' }}>
              <MenuSummaryWeekPicker
                locale={i18n.language}
                onChange={this.handleWeekChange}
                value={this.state.selectedWeekStart}
              />
            </div>
            {this.state.loading && (
              <GridItem md={12} style={{ textAlign: 'center' }}>
                <CircularProgress />
              </GridItem>
            )}
            <FormSelectSingle
              classes={classes}
              options={variantOptions}
              mapBy="displayName"
              trackBy="@id"
              value={this.state.variant}
              handleChange={this.handleSelectChange}
              label={this.props.t('form.menuPlanning.selectVariant')}
            />
          </GridItem>
          <GridItem md={4} style={{ textAlign: 'left' }}>
            <Button
              color="info"
              round={true}
              onClick={() => {
                this.modifyDate(true);
              }}
            >
              {this.props.t('form.menuPlanning.nextWeek')}
              <KeyboardArrowRight />
            </Button>
          </GridItem>
        </GridContainer>
        {filteredDiets.length > 0 && !this.state.loading && (
          <GridContainer>
            <GridItem md={12} style={{ textAlign: 'center' }}>
              {this.state.lockedExport && (
                <GridItem md={12} style={{ textAlign: 'center' }}>
                  <CircularProgress />
                </GridItem>
              )}
              {!this.state.lockedExport && (
                <Button
                  color="primary"
                  round={true}
                  onClick={() => {
                    this.exportToPDF();
                  }}
                >
                  {this.props.t('form.menuPlanning.saveWeekPdf')}
                </Button>
              )}
              {!this.state.lockedExport && (
                <Button
                  color="primary"
                  round={true}
                  onClick={() => {
                    this.exportToXLSX();
                  }}
                >
                  {this.props.t('form.menuPlanning.saveWeekExcel')}
                </Button>
              )}
            </GridItem>
          </GridContainer>
        )}
        <div style={{ overflow: 'auto' }}>
          <SummaryContent
            diets={filteredDiets}
            dates={this.getVisibleDays()}
            loading={this.state.loading}
            menuPlanners={this.state.menuPlanners}
            variant={this.state.variant}
          />
        </div>
      </AdminTable>
    ) : null;
  }
}

const mapStateToProps = state => ({
  variants: state.Variants.variants,
  diets: state.Diets.diets,
  brand: state.Brands.brand,
});

const mapDispatchToProps = dispatch => ({
  fetchMealTypes: () => dispatch(fetchMealTypes()),
  fetchVariants: () => dispatch(fetchVariants()),
  fetchDiets: () => dispatch(fetchDiets({ 'order[internalPosition]': 'ASC' })),
});

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withStyles(buttonsStyle)(Summary))
);
